import { Check } from 'lucide-react'
import react from 'react'
import { Form } from 'react-router'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card'
import {
	PRICING_CONFIG,
	PRICING_PLANS,
	PRICING_UTILS,
} from '#app/constants/pricing'
import { SignedIn } from './auth/SignedIn'
import { SignedOut } from './auth/SignedOut'

export function Pricing() {
	const [billingPeriod, setBillingPeriod] = react.useState<
		'monthly' | 'annual'
	>('monthly')

	const handleContactSales = () => {
		window.open(
			'mailto:sales@tasksentry.com?subject=Business Plan Inquiry',
			'_blank',
		)
	}

	const handleBillingPeriodChange = (period: 'monthly' | 'annual') => {
		setBillingPeriod(period)
	}

	return (
		<div className="mx-auto w-full max-w-5xl px-4">
			{/* Billing Toggle */}
			<div className="mb-12 flex justify-center">
				<div className="flex rounded-lg bg-gray-100 p-1">
					<button
						onClick={() => handleBillingPeriodChange('monthly')}
						className={`rounded-md px-6 py-2 text-sm font-medium transition-all duration-200 ${
							billingPeriod === 'monthly'
								? 'bg-white text-gray-900 shadow-sm'
								: 'text-gray-600 hover:text-gray-900'
						}`}
					>
						Monthly
					</button>
					<button
						onClick={() => handleBillingPeriodChange('annual')}
						className={`relative rounded-md px-6 py-2 text-sm font-medium transition-all duration-200 ${
							billingPeriod === 'annual'
								? 'bg-white text-gray-900 shadow-sm'
								: 'text-gray-600 hover:text-gray-900'
						}`}
					>
						Annual
						<span className="absolute -top-2 -right-2 rounded-full bg-green-500 px-1.5 py-0.5 text-xs text-white">
							{PRICING_CONFIG.ANNUAL_DISCOUNT_PERCENT}% off
						</span>
					</button>
				</div>
			</div>

			{/* Pricing Cards */}
			<div className="grid gap-10 md:grid-cols-2 lg:gap-12">
				{PRICING_PLANS.map((plan) => {
					const currentPrice =
						billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice
					const currentCtaText = plan.ctaText[billingPeriod]

					return (
						<Card
							key={plan.id}
							className={`relative transition-all duration-300 hover:-translate-y-1 ${
								plan.isPrimary
									? 'border-2 border-blue-500 shadow-xl shadow-blue-500/10'
									: 'border shadow-sm hover:border-gray-300 hover:shadow-md'
							}`}
						>
							{plan.badge && (
								<div className="absolute -top-3 left-1/2 -translate-x-1/2">
									<Badge className="border-0 bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1 text-white">
										{plan.badge}
									</Badge>
								</div>
							)}

							<CardHeader className="pb-6 pt-8 text-center">
								<CardTitle className="text-2xl font-bold text-gray-900">
									{plan.name}
								</CardTitle>
								<p className="mb-6 text-base text-gray-600">{plan.description}</p>

								<div className="mb-8">
									{currentPrice !== null ? (
										<>
											<div className="flex items-baseline justify-center gap-1">
												<span className="text-5xl font-bold text-gray-900">
													${currentPrice}
												</span>
												<span className="text-lg text-gray-600">
													/{billingPeriod === 'monthly' ? 'month' : 'year'}
												</span>
											</div>

											{billingPeriod === 'annual' && (
												<div className="mt-2">
													<span className="text-sm font-medium text-green-600">
														{currentPrice === 0
															? 'Free forever'
															: PRICING_UTILS.getFormattedSavings(
																	plan.monthlyPrice!,
																)}
													</span>
												</div>
											)}
										</>
									) : (
										<div className="flex items-baseline justify-center gap-1">
											<span className="text-2xl font-bold text-gray-900">
												Let's talk
											</span>
										</div>
									)}
								</div>

								{/* CTA Buttons */}
								<SignedOut>
									{plan.isContactSales ? (
										<Button
											size="lg"
											variant="outline"
											className="w-full"
											onClick={handleContactSales}
										>
											{currentCtaText}
										</Button>
									) : plan.priceIds ? (
										<Form method="POST" action="/api/stripe/checkout">
											<input
												type="hidden"
												name="priceId"
												value={
													billingPeriod === 'monthly'
														? plan.priceIds.monthly
														: plan.priceIds.annual
												}
											/>
											<input
												type="hidden"
												name="successUrl"
												value={
													typeof window !== 'undefined'
														? `${window.location.origin}/account?checkout=success`
														: '/account?checkout=success'
												}
											/>
											<input
												type="hidden"
												name="cancelUrl"
												value={
													typeof window !== 'undefined'
														? `${window.location.origin}/?checkout=cancel`
														: '/?checkout=cancel'
												}
											/>
											<Button
												type="submit"
												size="lg"
												className={`w-full ${
													plan.isPrimary
														? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
														: ''
												}`}
												variant={plan.isPrimary ? 'default' : 'outline'}
											>
												{currentCtaText}
											</Button>
										</Form>
									) : (
										<Button
											size="lg"
											variant="outline"
											className="w-full"
											asChild
										>
											<a href="/sign-up">{currentCtaText}</a>
										</Button>
									)}
								</SignedOut>

								<SignedIn>
									{plan.isContactSales ? (
										<Button
											size="lg"
											variant="outline"
											className="w-full"
											onClick={handleContactSales}
										>
											{currentCtaText}
										</Button>
									) : plan.priceIds ? (
										<Form method="POST" action="/api/stripe/checkout">
											<input
												type="hidden"
												name="priceId"
												value={
													billingPeriod === 'monthly'
														? plan.priceIds.monthly
														: plan.priceIds.annual
												}
											/>
											<input
												type="hidden"
												name="successUrl"
												value={
													typeof window !== 'undefined'
														? `${window.location.origin}/account?checkout=success`
														: '/account?checkout=success'
												}
											/>
											<input
												type="hidden"
												name="cancelUrl"
												value={
													typeof window !== 'undefined'
														? `${window.location.origin}/?checkout=cancel`
														: '/?checkout=cancel'
												}
											/>
											<Button
												type="submit"
												size="lg"
												className={`w-full ${
													plan.isPrimary
														? 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
														: ''
												}`}
												variant={plan.isPrimary ? 'default' : 'outline'}
											>
												{currentCtaText}
											</Button>
										</Form>
									) : (
										<Button
											size="lg"
											variant="outline"
											className="w-full"
											disabled
										>
											Current Plan
										</Button>
									)}
								</SignedIn>
							</CardHeader>

							<CardContent className="pt-0 pb-8">
								<div className="space-y-4">
									{plan.features.map((feature, featureIndex) => (
										<div key={featureIndex} className="flex items-start gap-4">
											{feature.available ? (
												<Check className="mt-1 h-6 w-6 flex-shrink-0 text-green-500" />
											) : (
												<div className="mt-1 h-6 w-6 flex-shrink-0" />
											)}
											<span
												className={`text-base ${
													feature.available ? 'text-gray-700' : 'text-gray-500'
												} flex items-center gap-2`}
											>
												{feature.text}
												{feature.comingSoon && (
													<Badge
														variant="secondary"
														className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 hover:bg-blue-100"
													>
														Coming Soon
													</Badge>
												)}
											</span>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)
				})}
			</div>

			{/* Additional Info */}
			<div className="mt-12 text-center">
				<p className="text-sm text-gray-600">
					Start with Free plan • Upgrade anytime to Pro for unlimited access
				</p>
			</div>
		</div>
	)
}
